import { ApiError } from "@/shared/models/ApiError";

/**
 * User-facing message for a failed service call.
 *
 * Prefers the backend body's `Error` / `error` / `message`, then RFC 7807
 * `errors`, `detail`, and `title`. An HTTP-status line covers other ApiErrors,
 * a plain Error uses its message, and anything else uses `fallback`.
 */
export function toServiceErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return apiErrorMessage(error);
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function apiErrorMessage(error: ApiError): string {
  const record = asRecord(error.body);
  const direct = directMessage(record);
  if (direct !== null) {
    return direct;
  }

  const problem = problemDetailsMessage(record);
  if (problem !== null) {
    return problem;
  }

  return `Request failed (${error.status})`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

/** `Error`, then `error`, then `message`. An empty string is not a message. */
function directMessage(record: Record<string, unknown> | null): string | null {
  if (record === null) {
    return null;
  }

  const message = record.Error ?? record.error ?? record.message;
  if (typeof message === "string" && message.length > 0) {
    return message;
  }

  return null;
}

/**
 * Field errors win over `detail` and `title`, so the stock validation title
 * does not replace the sentences the API wrote for the user.
 */
function problemDetailsMessage(record: Record<string, unknown> | null): string | null {
  if (record === null) {
    return null;
  }

  const fieldErrors = problemFieldErrors(record.errors);
  if (fieldErrors.length > 0) {
    return fieldErrors.join("; ");
  }

  const detail = nonEmptyString(record.detail);
  if (detail !== null) {
    return detail;
  }

  return nonEmptyString(record.title);
}

function problemFieldErrors(errors: unknown): string[] {
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) {
    return [];
  }

  const messages: string[] = [];
  for (const value of Object.values(errors as Record<string, unknown>)) {
    if (!Array.isArray(value)) {
      continue;
    }

    for (const entry of value) {
      const text = nonEmptyString(entry);
      if (text !== null) {
        messages.push(text);
      }
    }
  }

  return messages;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}
